import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, User, Users, Inbox, Loader2, CornerDownLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { BACKEND_URL } from '../../lib/backend';
import { ResourceIcon } from '../../pages/admin/ResourcePicker';
import { stageOf } from '../../pages/admin/crm/crmConstants';

const SECTIONS = [
  { key: 'users', label: 'Utenti' },
  { key: 'groups', label: 'Gruppi' },
  { key: 'leads', label: 'Lead (moduli)' },
  { key: 'resources', label: 'Risorse' },
];

const toItems = (data) => SECTIONS.flatMap(({ key }) => (data?.[key] || []).map((r) => {
  if (key === 'users') return { key, id: r.id, icon: <User className="w-4 h-4 text-blue-300" />, title: r.full_name || r.username, sub: [r.email, r.company_name].filter(Boolean).join(' · '), tag: r.role === 'lead' ? 'Lead' : 'Cliente', to: `/admin?tab=messaging&user=${r.id}` };
  if (key === 'groups') return { key, id: r.id, icon: <Users className="w-4 h-4 text-amber-300" />, title: r.name, sub: `${r.member_count} membri`, to: `/admin?tab=groups&group=${r.id}` };
  if (key === 'leads') return { key, id: r.id, icon: <Inbox className="w-4 h-4 text-amber-300" />, title: r.name || r.email, sub: [r.email, r.source].filter(Boolean).join(' · '), tag: stageOf({ pending: 'new', qualified: 'call_scheduled', closed: 'lost' }[r.status] || r.status).label, to: `/admin?tab=leads&lead=${r.id}` };
  return { key, id: `${r.kind}-${r.id}`, icon: <ResourceIcon kind={r.kind} className="w-4 h-4 text-cyan-300" />, title: r.title, sub: r.subtitle, to: r.link };
}));

const useSearch = (q, token) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setData(null); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/search`, { params: { q }, headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch { setData(null); }
      setLoading(false);
    }, 220);
    return () => clearTimeout(t);
  }, [q, token]);
  return { data, loading };
};

const ResultRow = ({ item, active, onHover, onPick }) => (
  <button
    type="button" onMouseEnter={onHover} onClick={onPick}
    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${active ? 'bg-amber-500/15' : 'hover:bg-white/5'}`}
    data-testid={`search-result-${item.key}-${item.id}`}
  >
    <span className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">{item.icon}</span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm text-white truncate">{item.title}</span>
      {item.sub && <span className="block text-xs text-slate-400 truncate">{item.sub}</span>}
    </span>
    {item.tag && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">{item.tag}</span>}
    {active && <CornerDownLeft className="w-3.5 h-3.5 text-amber-400" />}
  </button>
);

export const AdminSearchPalette = ({ open, onClose }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const { data, loading } = useSearch(q, token);
  const items = useMemo(() => toItems(data), [data]);

  useEffect(() => { if (open) { setQ(''); setTimeout(() => inputRef.current?.focus(), 30); } }, [open]);
  useEffect(() => setIdx(0), [items]);
  if (!open) return null;

  const pick = (item) => { onClose(); navigate(item.to); };
  const onKey = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(i + 1, items.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && items[idx]) pick(items[idx]);
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center pt-[12vh] px-4" data-testid="admin-search-palette">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="flex items-center gap-3 px-4 border-b border-white/10">
          <Search className="w-5 h-5 text-slate-400" />
          <input
            ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Cerca utenti, gruppi, lead, risorse…"
            className="flex-1 bg-transparent py-4 text-white text-base outline-none placeholder:text-slate-500"
            data-testid="admin-search-input"
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />}
          <kbd className="text-[10px] text-slate-400 border border-white/10 rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="admin-search-results">
          {q.trim().length < 2 && <p className="px-4 py-6 text-sm text-slate-500 text-center">Scrivi almeno 2 lettere: nome, email, azienda, titolo della risorsa…</p>}
          {q.trim().length >= 2 && !loading && data && items.length === 0 && (
            <p className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="admin-search-empty">Nessun risultato per "{q}"</p>
          )}
          {SECTIONS.map(({ key, label }) => {
            const group = items.filter((i) => i.key === key);
            if (!group.length) return null;
            return (
              <div key={key} className="pb-1">
                <p className="px-4 pt-2 pb-1 text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
                {group.map((item) => {
                  const i = items.indexOf(item);
                  return <ResultRow key={`${key}-${item.id}`} item={item} active={i === idx} onHover={() => setIdx(i)} onPick={() => pick(item)} />;
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
